// Service-worker registration — KILL-SWITCH MODE.
//
// SW-KILLSWITCH-MARKER — unique build/deploy verification string.
//
// The app used to register a caching service worker (/sw.js, app-shell +
// static precache). That SW persisted on providers' devices and kept
// serving the OLD cached app after every deploy — providers only saw new
// builds in Incognito. A booking app must never serve stale code.
//
// We no longer register a caching SW. Instead this module:
//   1. Unregisters any existing app-shell SW controlling this scope.
//   2. Deletes every Cache Storage entry that SW created.
//   3. Reloads ONCE (guarded) if the page was being controlled by the
//      old SW, so the current view drops its last SW-served response.
//
// `public/sw.js` is itself a one-shot self-destructing kill-switch, so
// devices that still fetch /sw.js as an "update" also self-clean. This
// module handles the case where the browser doesn't re-check /sw.js by
// unregistering from the app side directly.
//
// The push worker at scope /push/ (OneSignal-style) is a SEPARATE
// registration and is intentionally left alone here.

const RELOAD_FLAG = 'av_sw_killswitch_reloaded';

// register() keeps its old name + onUpdate signature so App.js imports
// don't change, but it no longer installs a caching SW. onUpdate is
// never invoked (there's no waiting SW to surface an update banner for).
export function register(/* { onUpdate } = {} */) {
  if (process.env.NODE_ENV !== 'production') return;
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    // If the self-destructing /sw.js asks us to reload after it evicts
    // caches, do it once (sessionStorage-guarded against a reload loop).
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SW_KILLSWITCH_RELOAD') {
        reloadOnce();
      }
    });

    purgeAppShellServiceWorker();
  });
}

function reloadOnce() {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return;
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch (_) {
    // If sessionStorage is unavailable, skip the reload rather than risk
    // a loop.
    return;
  }
  window.location.reload();
}

async function purgeAppShellServiceWorker() {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    let unregisteredAppShell = false;
    for (const reg of regs) {
      // Leave the push worker (scope .../push/) alone; only kill the
      // app-shell SW registered at the root scope.
      const scope = reg.scope || '';
      if (scope.includes('/push/')) continue;
      // Root-scope registration = the old app-shell SW (or the new
      // kill-switch mid-activation). Unregister it.
      try {
        const ok = await reg.unregister();
        if (ok) unregisteredAppShell = true;
      } catch (_) {}
    }

    // Evict any Cache Storage the app-shell SW left behind. Only touch
    // the old app-shell caches (prefixed 'avayble-v') so we never delete
    // anything a future feature might legitimately cache.
    if (typeof caches !== 'undefined' && caches.keys) {
      try {
        const keys = await caches.keys();
        await Promise.all(
          keys
            .filter((k) => k.startsWith('avayble-v'))
            .map((k) => caches.delete(k))
        );
      } catch (_) {}
    }

    // If we actually removed a controlling app-shell SW, reload once so
    // the page stops using its last SW-served response.
    if (unregisteredAppShell && navigator.serviceWorker.controller) {
      reloadOnce();
    }
  } catch (err) {
    // Never let SW cleanup crash app boot.
    console.warn('SW kill-switch cleanup failed (non-fatal):', err && err.message);
  }
}

// Kept for API compatibility with App.js's update-banner wiring. There is
// no waiting SW to activate anymore, so this just reloads.
export function applyUpdate(/* registration */) {
  window.location.reload();
}

// Kept for API compatibility. Unregisters any app-shell SW.
export function unregister() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  navigator.serviceWorker.getRegistrations().then((regs) => {
    for (const reg of regs) {
      if ((reg.scope || '').includes('/push/')) continue;
      reg.unregister().catch(() => {});
    }
  }).catch(() => {});
}
